import { noop } from '@silo/utils';
import { PROTOCOL_INSTANCE_MENU_CONFIG, useSiloWeb3 } from '@silo/web3';
import { PageProviders } from 'app/features/silo/PageProviders';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useRef } from 'react';
import { pipe } from 'fp-ts/lib/function';
import { option } from 'fp-ts';
import { ProtocolInstanceAsUrl, isRuntimeProtocolInstance } from '@silo/types';
import { EmptyText, Modal, SiloLogoIcon } from '@silo/ui';

export default function ProtocolInstanceRedirect() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { protocolInstance, setProtocolInstance, isAttemptingChainUpdate } = useSiloWeb3();
  const { query, replace, isReady } = useRouter();

  const routerProtocolInstance = pipe(
    query.protocolInstance,
    ProtocolInstanceAsUrl.decode,
    option.fromEither,
    option.filter(isRuntimeProtocolInstance),
    option.toUndefined
  );

  const redirectDestination = (query.redirect as string) ?? '/';

  useEffect(() => {
    const redirect = () => {
      replace(redirectDestination);
    };

    if (!isReady || !protocolInstance) return;

    if (!routerProtocolInstance) return redirect();

    if (routerProtocolInstance === protocolInstance) {
      redirect();
    } else {
      setProtocolInstance(routerProtocolInstance, true);
    }
  }, [isReady, protocolInstance, routerProtocolInstance, redirectDestination, replace, setProtocolInstance]);

  return (
    <Modal isOpen closeModal={noop} variant="transparent" notDismissable>
      <div className="flex flex-col gap-5">
        <div>
          <SiloLogoIcon className="text-white h-9" />
        </div>

        {isAttemptingChainUpdate && routerProtocolInstance ? (
          <div className="flex flex-col gap-5">
            <h1 className="text-xl font-medium outline-none" tabIndex={-1} ref={headingRef}>
              Switching network
            </h1>

            <p>Your connected wallet is on a different network than the one you are trying to access Silo on.</p>

            <p>
              To access Silo{' '}
              <span className="font-bold">{PROTOCOL_INSTANCE_MENU_CONFIG[routerProtocolInstance].label}</span>, please
              go to your wallet to confirm the network switch request.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 animate-pulse">
            <div className="rounded-lg bg-shale-800 w-fit">
              <EmptyText className="text-xl font-medium ">Switching network</EmptyText>
            </div>

            <div className="rounded-lg bg-shale-800 ">
              {[...Array(5)].map(() => (
                <EmptyText />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

ProtocolInstanceRedirect.Layout = Fragment;
ProtocolInstanceRedirect.Provider = PageProviders;
